import React, { useEffect, useState } from 'react';
import JsBarcode from 'jsbarcode';
import styles from './index.module.scss';
import { getImageFromS3 } from '../../../imageService';
import sgmService from '../../../services/sgmService';

const isImageSrc = (value) =>
  typeof value === 'string' &&
  (/^https?:\/\//i.test(value) || /^data:image\//i.test(value));

const firstDefined = (...values) =>
  values.find((value) => value !== undefined && value !== null && value !== '');

const getFirstTopUp = (source) => {
  if (!source) return undefined;

  const list =
    source.rewards?.CertificatesTopUp ||
    source.CertificatesTopUp ||
    source.certificate?.CertificatesTopUp ||
    source.reward?.CertificatesTopUp;

  return Array.isArray(list) && list.length ? list[0] : undefined;
};

const isValidEan13 = (value) => {
  if (!/^\d{13}$/.test(value)) return false;

  const digits = value.split('').map(Number);
  const checksum = digits
    .slice(0, 12)
    .reduce((sum, digit, index) => sum + digit * (index % 2 === 0 ? 1 : 3), 0);

  return (10 - (checksum % 10)) % 10 === digits[12];
};

const getPreferredBarcodeFormat = (value) =>
  isValidEan13(value) ? 'EAN13' : 'CODE128';

const getFileExtension = (mimeType) => {
  if (!mimeType) return 'png';
  if (mimeType.includes('pdf')) return 'pdf';
  if (mimeType.includes('jpeg') || mimeType.includes('jpg')) return 'jpg';
  if (mimeType.includes('webp')) return 'webp';
  if (mimeType.includes('gif')) return 'gif';
  return 'png';
};

const BARCODE_OPTIONS = {
  width: 3,
  height: 110,
  margin: 15,
  displayValue: true,
  fontSize: 18,
  textMargin: 10,
  textAlign: 'center',
  font: 'Roboto, sans-serif',
  background: '#ffffff',
  lineColor: '#000000',
  flat: true
};

const BarcodeComponent = ({
  selectedCertificate,
  setScreenNumber,
  onBack,
  onQuestionClick
}) => {
  const [balance, setBalance] = useState(0);
  const [reward, setReward] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const [barcodeDataUrl, setBarcodeDataUrl] = useState('');
  const [isDownloading, setIsDownloading] = useState(false);

  useEffect(() => {
    (async () => {
      const balanceData = await sgmService.getBalance();
      setBalance(balanceData?.balance || 0);
    })();
  }, []);

  useEffect(() => {
    const rewardId = selectedCertificate?.id;

    if (!rewardId) {
      setIsLoading(false);
      return;
    }

    (async () => {
      try {
        setIsLoading(true);
        setHasError(false);
        const data = await sgmService.getRewardById(rewardId);
        setReward(data || null);
      } catch (e) {
        setHasError(true);
      } finally {
        setIsLoading(false);
      }
    })();
  }, [selectedCertificate?.id]);

  const handleBack = () => (onBack ? onBack() : setScreenNumber(4));

  const topUp = getFirstTopUp(selectedCertificate) || getFirstTopUp(reward);

  const rawBarcode = firstDefined(
    topUp?.barcode,
    reward?.barcode,
    reward?.bar_code,
    reward?.code
  );
  const barcodeImageSrc = firstDefined(
    reward?.barcode_url,
    reward?.barcodeImage,
    reward?.barcode_image,
    isImageSrc(rawBarcode) ? rawBarcode : undefined
  );
  const barcodeValue = isImageSrc(rawBarcode)
    ? firstDefined(reward?.card_number, reward?.serial, reward?.serial_number)
    : firstDefined(rawBarcode, reward?.card_number, reward?.serial);

  const downloadUrl = firstDefined(
    topUp?.certUrl,
    reward?.certificate_url,
    reward?.download_url,
    reward?.pdf_url,
    reward?.file_url
  );

  useEffect(() => {
    if (!barcodeValue) {
      setBarcodeDataUrl('');
      return;
    }

    const value = String(barcodeValue).trim();

    const render = (format) => {
      let isValid = true;
      const canvas = document.createElement('canvas');

      JsBarcode(canvas, value, {
        ...BARCODE_OPTIONS,
        format,
        valid: (result) => {
          isValid = result;
        }
      });

      return isValid ? canvas.toDataURL('image/png') : null;
    };

    try {
      const preferred = getPreferredBarcodeFormat(value);
      const dataUrl =
        (preferred !== 'CODE128' && render(preferred)) || render('CODE128');
      setBarcodeDataUrl(dataUrl || '');
    } catch (e) {
      setBarcodeDataUrl('');
    }
  }, [barcodeValue]);

  const handleDownloadCertificate = async () => {
    if (!downloadUrl || isDownloading) return;

    setIsDownloading(true);

    try {
      const response = await fetch(downloadUrl);
      if (!response.ok) throw new Error(`Request failed: ${response.status}`);

      const blob = await response.blob();
      const objectUrl = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = objectUrl;
      link.download = `certificate-${barcodeValue || 'iqos'}.${getFileExtension(
        blob.type
      )}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      setTimeout(() => URL.revokeObjectURL(objectUrl), 2000);
    } catch (e) {
      // fetch was blocked (usually cross-origin CORS): fall back to a direct
      // anchor with the download attribute. A same-origin file downloads
      // automatically; for a cross-origin file whose server lacks CORS the
      // browser ignores `download` and opens it — that case can only be forced
      // server-side with a `Content-Disposition: attachment` header.
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = `certificate-${barcodeValue || 'iqos'}.png`;
      link.target = '_blank';
      link.rel = 'noopener noreferrer';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } finally {
      setIsDownloading(false);
    }
  };

  const barcodeSrc =
    barcodeImageSrc ||
    barcodeDataUrl ||
    getImageFromS3('/images/mgm/barcode.png');

  return (
    <div className={styles.container}>
      <img
        src={getImageFromS3('/images/mgm/bg.png')}
        alt="background"
        className={styles.backgroundImage}
      />
      <img
        className={styles.glassHeadBg}
        src={getImageFromS3('/images/mgm/big-glass-head-bg.png')}
        alt="glass-head"
      />
      <div className={styles.content}>
        <div className={styles.header}>
          <svg
            width="20"
            height="18"
            viewBox="0 0 20 18"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            onClick={handleBack}
          >
            <path
              d="M4.29675 8.59375L9.66769 13.9647C10.261 14.5579 10.261 15.5198 9.66769 16.1131C9.07444 16.7063 8.11258 16.7063 7.51932 16.1131L0.707108 9.30086C0.316583 8.91033 0.316583 8.27717 0.707107 7.88664L7.51932 1.07443C8.11258 0.481175 9.07444 0.481175 9.66769 1.07443C10.261 1.66769 10.261 2.62955 9.66769 3.22281L4.29675 8.59375Z"
              fill="white"
            />
            <rect
              x="2.78076"
              y="6.99902"
              width="17"
              height="3"
              rx="1.5"
              fill="white"
            />
          </svg>
          <div onClick={onQuestionClick}>
            Є питання?
            <svg
              width="27"
              height="21"
              viewBox="0 0 27 21"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M1.86207 0C0.837931 0 0 0.821739 0 1.82609V16.4348L2.7931 13.6957H20.4828C21.5069 13.6957 22.3448 12.8739 22.3448 11.8696V1.82609C22.3448 0.821739 21.5069 0 20.4828 0H1.86207ZM23.2759 7.30435V11.8696C23.2759 13.4217 22.0655 14.6087 20.4828 14.6087H13.0345V16.4348C13.0345 17.4391 13.8724 18.2609 14.8966 18.2609H24.2069L27 21V9.13043C27 8.12609 26.1621 7.30435 25.1379 7.30435H23.2759Z"
                fill="white"
              />
            </svg>
          </div>
        </div>
        <div className={styles.pointBalanceContainer}>
          <p>
            Баланс балів <b>{balance}</b>
          </p>
        </div>
        <h1>Ваш сертифікат</h1>
        {isLoading ? (
          <p className={styles.loading}>Завантаження...</p>
        ) : (
          <>
            <div className={styles.barCodeWrapper}>
              <img src={barcodeSrc} alt="barcode" />
            </div>
            <button
              className={styles.downloadCertificate}
              onClick={handleDownloadCertificate}
              disabled={!downloadUrl || isDownloading}
              style={
                !downloadUrl || isDownloading ? { opacity: 0.6 } : undefined
              }
            >
              {isDownloading ? 'Завантаження...' : 'Завантажити сертифікат'}
            </button>
            {hasError && (
              <p className={styles.errorText}>
                Не вдалося завантажити дані сертифіката. Спробуйте пізніше.
              </p>
            )}
          </>
        )}
        <h2>
          Ваші куплені сертифікати знаходяться у вкладці{' '}
          <b>“Мої сертифікати”</b>
        </h2>
        <div className={styles.footer}>
          <button onClick={() => setScreenNumber(2)}>Правила програм</button>
          <button
            onClick={() =>
              window.open(
                'https://www.pmiprivacy.com/ukraine/uk/consumer/',
                '_blank'
              )
            }
          >
            Політика конфіденційності
          </button>
          <button onClick={() => setScreenNumber(10)}>FAQ</button>
          <button onClick={() => setScreenNumber(11)}>
            Параметри застосування файлів cookie
          </button>
        </div>
      </div>
    </div>
  );
};

export default BarcodeComponent;
